// The "All Receipts" table — shared by the Proxy/CA/Issuer tab and the
// Regulatory tab (MaterialReceiptsPage.tsx) so both render identically
// (period band, count/category/expand-all/search row, month grouping,
// edit modal, PDF panel) and don't fall out of sync.
import React, { useState, useEffect, useMemo } from 'react';
import { updateMaterialReceipt } from '../services/materialReceiptService';
import type { MaterialReceiptRow } from '../types/api';
import { SearchInput } from './SearchInput';
import { SortableTh } from './SortableTh';
import { PageHeader } from './PageHeader';
import { useSort, sortRows } from '../utils/useSort';
import { pillStyle } from '../utils/pillStyle';
import { CATEGORY_LABEL, CATEGORY_PILL } from '../utils/jobCategory';
import { windowBounds, inWindow, windowCounts, type ScopeValue } from '../utils/timeWindow';
import { groupByMonth, useGroupCollapse } from '../utils/monthGroups';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';
const INP = 'w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white';
const CATEGORY_CLS: Record<string, string> = { P: 'tp tp-px', CA: 'tp tp-ca', R: 'tp tp-rg', I: 'tp tp-px' };
const COLUMNS = ['Received', 'Category', 'Scan #', 'Client Name(s)'];

// Sort key per column — raw ISO for dates so they order chronologically.
function cellValue(r: MaterialReceiptRow, column: string): string {
  switch (column) {
    case 'Received':        return r.intake_date ?? '';
    case 'Category':        return r.job_category ? (CATEGORY_LABEL[r.job_category] ?? r.job_category) : '';
    case 'Scan #':          return r.scan_number;
    case 'Client Name(s)':  return r.client_names.join(', ');
    default:                return '';
  }
}

export function CategoryTag({ category }: { category: string | null }) {
  if (!category) return <span className="text-xs text-muted">—</span>;
  return <span className={CATEGORY_CLS[category] ?? 'tp tp-px'}>{CATEGORY_LABEL[category] ?? category}</span>;
}

export function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${parseInt(m)}/${parseInt(d)}/${y}`;
}

export function EditModal({ row, onClose, onSaved, showToast }: { row: MaterialReceiptRow; onClose: () => void; onSaved: (r: MaterialReceiptRow) => void; showToast?: (m: string) => void }) {
  const [scan, setScan] = useState(row.scan_number);
  const [category, setCategory] = useState(row.job_category ?? 'P');
  const [clients, setClients] = useState(row.client_names.join(', '));
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const updated = await updateMaterialReceipt(row.id, {
        scan_number: scan,
        job_category: category,
        client_names: clients.split(',').map(s => s.trim()).filter(Boolean),
      });
      onSaved(updated);
      showToast?.('Receipt updated');
      onClose();
    } catch {
      showToast?.('Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(4,50,67,.5)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-modal w-[460px] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-trim">
          <span className="text-base font-semibold text-ink">Edit Receipt</span>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-copy hover:bg-wash">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
        </div>
        <div className="px-5 py-4 flex flex-col gap-3">
          <div>
            <label className="block text-xs font-medium text-copy uppercase tracking-wide mb-1">Scan Number</label>
            <input className={INP} value={scan} onChange={e => setScan(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-copy uppercase tracking-wide mb-1">Job Category</label>
            <select className={INP} value={category} onChange={e => setCategory(e.target.value)}>
              <option value="P">Proxy</option>
              <option value="CA">Corp Action</option>
              <option value="I">Issuer</option>
              <option value="R">Regulatory</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-copy uppercase tracking-wide mb-1">Client Name(s) — comma separated</label>
            <input className={INP} value={clients} onChange={e => setClients(e.target.value)} />
          </div>
        </div>
        <div className="px-5 pb-5 pt-3 border-t border-trim flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-trim text-sm font-medium text-copy hover:bg-wash">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

export function PdfPanel({ row, onClose }: { row: MaterialReceiptRow; onClose: () => void }) {
  return (
    <div className="flex-shrink-0 border-l border-trim bg-white flex flex-col" style={{ width: 420 }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-trim">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink truncate">{row.scan_number}</div>
          <div className="text-xs text-muted truncate">{row.client_names.join(', ')}</div>
        </div>
        <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-copy hover:bg-wash">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>
      <div className="flex-1 min-h-0 bg-[#F5F7FA]">
        <object data={`${API_BASE}${row.file_uri}`} type="application/pdf" className="w-full h-full">
          <div className="flex flex-col items-center justify-center h-full text-center px-6 text-muted text-sm gap-1">
            <span className="font-medium text-ink">PDF preview unavailable</span>
            <span>{row.file_page_count} page(s) · {row.scan_number}</span>
            <span className="text-xs">(demo placeholder — no source file)</span>
          </div>
        </object>
      </div>
    </div>
  );
}

export interface CategoryFilterOption { id: string; label: string }

interface MaterialReceiptsTableProps {
  rows: MaterialReceiptRow[];
  categoryFilters?: CategoryFilterOption[];
  onSaved: (r: MaterialReceiptRow) => void;
  openAce: (r: MaterialReceiptRow) => void;
  showToast?: (m: string) => void;
  collapseStorageKey: string;
  emptyMessage: string;
  loading?: boolean;
  initialViewReceiptId?: string | null;
}

// Self-contained receipts table: period band, count badge, optional category
// pills, expand-all, search, month-grouped rows, edit modal and PDF panel.
// Each instance owns its own scope/search/sort/collapse state, so the Proxy
// and Regulatory tabs (each rendering one instance) don't share filters.
export function MaterialReceiptsTable({
  rows, categoryFilters, onSaved, openAce, showToast, collapseStorageKey, emptyMessage, loading = false, initialViewReceiptId,
}: MaterialReceiptsTableProps) {
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<ScopeValue>({ window: 'custom' });
  const { sort, toggle } = useSort({ column: 'Received', direction: 'desc' });
  const [editRow, setEditRow] = useState<MaterialReceiptRow | null>(null);
  const [viewRow, setViewRow] = useState<MaterialReceiptRow | null>(null);

  // Deep-link from the Receiving Log "view file" action.
  useEffect(() => {
    if (initialViewReceiptId && rows.length) {
      const r = rows.find(x => x.id === initialViewReceiptId);
      if (r) setViewRow(r);
    }
  }, [initialViewReceiptId, rows]);

  // No preset/range selected → show everything; All Receipts is a lookup list,
  // not a daily operational view, so there's no reason to default-scope to "today".
  const windowedRows = useMemo(() => {
    if (scope.window === 'custom' && !scope.from) return rows;
    const bounds = windowBounds(scope);
    return rows.filter(r => inWindow(r.intake_date, bounds));
  }, [rows, scope]);

  const counts = useMemo(() => windowCounts(rows), [rows]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const data = windowedRows.filter(r => {
      if (categoryFilters && category !== 'All' && r.job_category !== category) return false;
      if (q && !`${r.client_names.join(' ')} ${r.scan_number}`.toLowerCase().includes(q)) return false;
      return true;
    });
    return sortRows(data, sort, cellValue);
  }, [windowedRows, categoryFilters, category, search, sort]);

  // Grouping only reads as chronological when sorted by Received; any other
  // column sort shows a flat list instead.
  const groups = useMemo(() => sort.column === 'Received' ? groupByMonth(filtered, r => r.intake_date) : null, [filtered, sort.column]);
  const { collapsed, toggleGroup, toggleAll } = useGroupCollapse(collapseStorageKey, groups);

  const renderRow = (r: MaterialReceiptRow) => (
    <tr key={r.id} className="group hover:bg-wash transition-colors cursor-pointer" onClick={() => openAce(r)}>
      <td className="px-2 py-1.5 border-b border-wash text-xs text-muted whitespace-nowrap align-top">{fmtDate(r.intake_date)}</td>
      <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top"><CategoryTag category={r.job_category} /></td>
      <td className="px-2 py-1.5 border-b border-wash text-xs font-medium text-ink whitespace-nowrap align-top">{r.scan_number}</td>
      <td className="px-2 py-1.5 border-b border-wash align-top">
        <div className="max-w-[280px] text-xs text-copy break-words" title={r.client_names.join(', ')}>{r.client_names.join(', ')}</div>
      </td>
      <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-0.5">
          <button onClick={() => setEditRow(r)} title="Edit" className="w-7 h-7 rounded flex items-center justify-center text-copy hover:bg-wash hover:text-ink">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
          </button>
          <button onClick={() => setViewRow(r)} title="View file" className="w-7 h-7 rounded flex items-center justify-center text-copy hover:bg-wash hover:text-ink">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="flex-1 flex overflow-hidden">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Global header — period band, full width */}
        <PageHeader scope={scope} onScopeChange={setScope} counts={counts} />

        {/* Filter row — receipt count, optional category pills, expand-all, then search */}
        <div className="flex items-center gap-3 px-6 pt-4 pb-4 border-b border-trim flex-shrink-0 flex-wrap gap-y-2">
          <div className="px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: '#F5F7FA', color: '#595A60' }}>{filtered.length} receipts</div>
          {categoryFilters && (
            <div className="flex gap-1.5 flex-wrap">
              {categoryFilters.map(c => (
                <button key={c.id} onClick={() => setCategory(c.id)} className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
                  style={pillStyle(category === c.id, CATEGORY_PILL[c.id])}>{c.label}</button>
              ))}
            </div>
          )}
          {groups && groups.length > 1 && (
            <button
              onClick={toggleAll}
              className="text-xs font-medium text-teal-600 hover:text-teal-700"
            >
              {collapsed.size ? 'Expand all' : 'Collapse all'}
            </button>
          )}
          <div className="ml-auto">
            <SearchInput value={search} onChange={setSearch} placeholder="Search clients, scans…" width={220} />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto px-6 pb-6">
          {loading && <div className="text-sm text-muted py-4">Loading…</div>}
          <table className="text-xs" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
            <thead>
              <tr>
                {COLUMNS.map(h => (
                  <SortableTh key={h} label={h} column={h} sort={sort} onSort={toggle} />
                ))}
                <SortableTh label="Actions" sort={sort} onSort={toggle} />
              </tr>
            </thead>
            <tbody>
              {groups
                ? groups.map(g => {
                    const isCollapsed = collapsed.has(g.key);
                    return (
                      <React.Fragment key={g.key}>
                        <tr className="bg-white sticky z-[5]" style={{ top: 33 }}>
                          <td colSpan={COLUMNS.length + 1} className="p-0 border-b border-trim">
                            <button
                              onClick={() => toggleGroup(g.key)}
                              className="flex items-center gap-2 w-full text-left px-2 py-1.5 hover:bg-wash transition-colors"
                            >
                              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={`text-muted transition-transform ${isCollapsed ? '-rotate-90' : ''}`}>
                                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              <span className="text-xs font-semibold text-ink">{g.label}</span>
                              <span className="px-1.5 rounded-full text-[11px] font-semibold tabular-nums leading-[1.4]" style={{ background: '#F1F5F9', color: '#73747A' }}>{g.rows.length}</span>
                            </button>
                          </td>
                        </tr>
                        {!isCollapsed && g.rows.map(renderRow)}
                      </React.Fragment>
                    );
                  })
                : filtered.map(renderRow)}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={COLUMNS.length + 1} className="text-center py-12 text-muted text-sm">{emptyMessage}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewRow && <PdfPanel row={viewRow} onClose={() => setViewRow(null)} />}
      {editRow && <EditModal row={editRow} onClose={() => setEditRow(null)} onSaved={onSaved} showToast={showToast} />}
    </div>
  );
}
